import { IMPERIAL_BEACH_SAMPLE } from '@/constants';
import { DeckardCognitoUser } from '@/user/deckard-cognito-user';
import { getPlaceStateAndCityClassification } from '@/utils/data-utils';
import { toaster } from '@/utils/toaster';
import { BinaryRenderer } from '@/widgets/components/binary-renderer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Radio, Toggle } from 'rsuite';
import styled from 'styled-components';
import { AnyCityPicker } from './any-city-picker';
import {
  StyledDropdownWrapper,
  StyledRadioGroup,
} from './city-selection-styles';
import { CITY, COUNTY } from './user-profile-constants';

const Header = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const Title = styled.div`
  font-weight: bold;
`;

const IncludeCitiesToggle = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 15px;
`;

const ModeLabelSpan = styled.span`
  font-weight: 600;
  margin-right: 10px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
`;

const handleUserSavedDefault = async (currentCity) => {
  await DeckardCognitoUser.setDefaultCity(currentCity);
  toaster.success(`Default city changed to: ${currentCity}`);
};

function RegionOptions(props) {
  return (
    <StyledRadioGroup
      value={props.currRadio}
      onChange={props.handleRadioOnClick}
    >
      <Radio data-testid="admin-dashboard-region-level-county" value={COUNTY}>
        County
      </Radio>
      <Radio data-testid="admin-dashboard-region-level-city" value={CITY}>
        City
      </Radio>
    </StyledRadioGroup>
  );
}

/**
 * This component allows the user to select the current city. Hides when there's only one city
 *
 * @param {*} props
 */
export function PlaceSelection(props) {
  /**
   * `current...` variables contain what is being used by Rentalscape at the moment, vs.
   * `selected...` variables that contain what the user selected in the dropdown,
   * but may not have saved with the "Apply" button yet.
   */
  const { currentIncludeCities, currentPlace, onCurrentPlaceAndModeChange } =
    props;
  const [displayedPlacesType, setDisplayedPlacesType] = useState(COUNTY);
  const [selectedPlaceDetails, setSelectedPlaceDetails] = useState({});
  const [selectedIncludeCities, setSelectedIncludeCities] =
    useState(currentIncludeCities);

  const currentPlaceDetails = useMemo(() => {
    const { state, placeType } =
      getPlaceStateAndCityClassification(currentPlace);
    return { state, placeType, place: currentPlace };
  }, [currentPlace]);

  const handleRadioOnClick = (radio) => {
    setDisplayedPlacesType(radio);
  };
  /**
   * Resets `selectedPlaceDetails` every time the
   * user changes between city/county or advanced/back button
   */
  useEffect(() => {
    const hasPlaceTypeChanged =
      displayedPlacesType !== selectedPlaceDetails?.placeType;
    setSelectedPlaceDetails({
      ...selectedPlaceDetails,
      placeType: displayedPlacesType,
      place: hasPlaceTypeChanged ? null : selectedPlaceDetails?.place,
    });
  }, [displayedPlacesType]);

  const resetPlace = useCallback((placeDetails) => {
    setSelectedPlaceDetails(placeDetails);
    setDisplayedPlacesType(placeDetails.placeType);
  }, []);

  useEffect(() => {
    resetPlace(currentPlaceDetails);
  }, [currentPlaceDetails]);

  useEffect(() => {
    setSelectedIncludeCities(currentIncludeCities);
  }, [currentIncludeCities]);

  useEffect(() => {
    /*
      If the user is in the middle of searching for a new place (e.g.
      they've changed the city/county switch or the state, and the place dropdown
      does not have a value), and they flip "Include Cities" switch, reset the place
      dropdowns to the currently-selected place in the app.
     */
    if (!selectedPlaceDetails.place && currentPlaceDetails) {
      resetPlace(currentPlaceDetails);
    }
  }, [selectedIncludeCities, currentPlaceDetails]);

  // Handle click on button to go directly to Imperial Beach Dummy Data
  const handleClick = () => {
    handleUserSavedDefault(IMPERIAL_BEACH_SAMPLE);
    onCurrentPlaceAndModeChange(IMPERIAL_BEACH_SAMPLE, selectedIncludeCities);
  };

  const hasSelectedDifferentPlace =
    selectedPlaceDetails?.place !== currentPlace;
  const hasIncludeCitiesChanged =
    selectedIncludeCities !== currentIncludeCities;

  return (
    <StyledDropdownWrapper className={props.className}>
      <Header>
        <Title>Set Client</Title>
        <button onClick={handleClick}>Imperial Beach Demo</button>
      </Header>
      <div>Data date: {props.stats?.dataDate}</div>
      <RegionOptions
        handleRadioOnClick={handleRadioOnClick}
        currRadio={displayedPlacesType}
      />

      <AnyCityPicker
        cityOrCounty={displayedPlacesType}
        selectedPlace={selectedPlaceDetails}
        setSelectedPlace={setSelectedPlaceDetails}
      />

      <IncludeCitiesToggle>
        <Toggle
          size="sm"
          onChange={setSelectedIncludeCities}
          checked={selectedIncludeCities}
        />
        <ModeLabelSpan>Include Cities</ModeLabelSpan>
      </IncludeCitiesToggle>

      <ActionButtons>
        <BinaryRenderer checkpoint={Boolean(selectedPlaceDetails?.place)}>
          <BinaryRenderer
            checkpoint={hasSelectedDifferentPlace || hasIncludeCitiesChanged}
          >
            <button
              data-testid="admin-dashboard-place-change-apply"
              css="margin-top: 10px; align-self: flex-start;"
              onClick={() => {
                onCurrentPlaceAndModeChange(
                  selectedPlaceDetails.place,
                  selectedIncludeCities
                );
              }}
            >
              Apply
            </button>
          </BinaryRenderer>
          <button
            css="margin-top: 10px; align-self: flex-start;"
            onClick={() => {
              handleUserSavedDefault(selectedPlaceDetails.place);
            }}
          >
            Set as Default City
          </button>
        </BinaryRenderer>
      </ActionButtons>
    </StyledDropdownWrapper>
  );
}
